import { useEffect, useMemo, useState } from "react"

import { App, Flex, Modal, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"
import ReactPlayer from "react-player"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import Checkbox from "../../../Components/Checkbox"
import Icon from "../../../Icon"
import { modalConfig } from "../../../theme"
import {
  getBusinessPathByStepId,
  getOnboardingPathByStepId
} from "../../../Utils/onboarding/onboardingFlow"
import {
  ALL_FEATURES,
  CHOICE_FLOWS,
  FEATURES,
  hasOnboardingProgress,
  resolveProductChoice,
  sameChoice,
  saveProductChoice
} from "../../../Utils/onboarding/productChoice"
import { getApplicableSteps, nextApplicableStepId } from "../../../Utils/onboarding/stepSequence"
import { BA_STEPS } from "../../GettingStartedPage/utils/businessSteps"
import { OB_STEPS } from "../../GettingStartedPage/utils/onboardingSteps"
import { updateProfile } from "../../LoginPage/redux/auth.actions"
import { restartCreateAccountAtCompanyStep } from "../../User/redux/user.actions"
import { updateCompletedStep, updateUserInfo } from "../../User/services"

import "./styles.less"

const { Text } = Typography

// Copy follows the FRD (OB-4-04, OB-4-05). Videos are per product; none exists yet, so the
// "Watch video" action is only rendered for a card that has one.
const FEATURE_OPTIONS = [
  {
    key: FEATURES.PREVENT,
    icon: "alerts",
    title: "Prevent",
    description:
      "Stop chargebacks before they happen with real-time alerts that fire when a cardholder disputes a charge, so it can be resolved before it becomes a chargeback.",
    videoUrl: null
  },
  {
    key: FEATURES.RECOVER,
    icon: "receipt_check",
    title: "Recover",
    description:
      "Fight disputes and recover revenue by submitting evidence straight from your connected processors.",
    videoUrl: null
  }
]

const OnboardingFeaturesPage = () => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const userInfo = useSelector((state) => state.auth?.userInfo)
  const nextCreateAccountStep = useSelector((state) => state.user.nextCreateAccountStep)
  const businessAccount = useSelector((state) => state.user.businessAccount)
  const createAccountBusinessId = useSelector((state) => state.user.createAccountBusinessId)

  const isBusinessFlow = pathname.includes("/create-account")
  const flow = isBusinessFlow ? CHOICE_FLOWS.BUSINESS : CHOICE_FLOWS.ONBOARDING
  const hasProgress = hasOnboardingProgress({ flow, userInfo, nextCreateAccountStep })

  // What the flow currently believes: a stored answer, Prevent only for an account that started
  // before this screen existed (OB-X-07), or nothing yet. Captured once so a change can be
  // recognised on Continue.
  const recordedChoice = useMemo(
    () => resolveProductChoice({ flow, hasProgress }),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    []
  )

  // Both cards on the first visit — the recommended setup, not an assumption (OB-4-06); the
  // recorded choice on a revisit (OB-4-07).
  const [selected, setSelected] = useState(() => recordedChoice ?? ALL_FEATURES)
  const [videoUrl, setVideoUrl] = useState(null)

  useEffect(() => {
    document.title = "Choose your features - Chargeback"
  }, [])

  const toggle = (key) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))

  // Where Continue lands: the first step that still applies at or after the flow's own pointer.
  // A merchant who comes back here and changes nothing returns to where they were.
  const nextPath = (choice) => {
    const steps = getApplicableSteps({ choice })

    if (isBusinessFlow) {
      const stepId = nextApplicableStepId({
        stepId: Math.max(nextCreateAccountStep ?? BA_STEPS.COMPANY, BA_STEPS.COMPANY),
        steps,
        stepMap: BA_STEPS
      })

      return getBusinessPathByStepId(stepId)
    }

    const stepId = nextApplicableStepId({
      stepId: Math.max(userInfo?.next_step ?? OB_STEPS.COMPANY, OB_STEPS.COMPANY),
      steps,
      stepMap: OB_STEPS
    })

    return getOnboardingPathByStepId(stepId)
  }

  const proceed = (choice) => {
    saveProductChoice(choice, flow)
    navigate(nextPath(choice))
  }

  // Switching products after later steps were done resets the flow to the step after this one
  // (OB-4-M03..M07). The create-account reducer clears the in-progress business's draft data on
  // that step change but keeps the business itself, so Company profile updates the one this flow
  // already created rather than posting a second one (CHA1-3282); the primary flow moves the
  // saved pointer back. The business's own progress (`completed_steps`) is server-side too: the
  // admin table and every resume read it, so it goes back to "Company profile done" as well —
  // otherwise the reset only lived in this tab (CHA1-3541).
  const resetAndProceed = (choice) => {
    if (isBusinessFlow) {
      // Read before the restart: it clears the draft, `businessAccount` included.
      const businessId = businessAccount?.id ?? createAccountBusinessId

      dispatch(restartCreateAccountAtCompanyStep())
      if (businessId) {
        updateCompletedStep({ step: BA_STEPS.COMPANY, business_account_id: businessId })
      }
    } else {
      // Saved directly rather than through the user-save saga: that saga publishes a
      // `saveResult` the Company profile page reads as "business saved, move on", which would
      // skip the very step the reset just returned the merchant to.
      dispatch(updateProfile({ next_step: OB_STEPS.COMPANY }))
      updateUserInfo({ next_step: OB_STEPS.COMPANY }).then(() =>
        // Applied again once saved: a profile refetch racing the request would otherwise put
        // the old pointer back until the next reload.
        dispatch(updateProfile({ next_step: OB_STEPS.COMPANY }))
      )
    }

    saveProductChoice(choice, flow)
    navigate(
      isBusinessFlow
        ? getBusinessPathByStepId(BA_STEPS.COMPANY)
        : getOnboardingPathByStepId(OB_STEPS.COMPANY)
    )
  }

  const handleContinue = () => {
    const changed = recordedChoice !== null && !sameChoice(selected, recordedChoice)

    if (!hasProgress || !changed) {
      proceed(selected)

      return
    }

    modal.confirm({
      ...modalConfig("exclamation", 442),
      centered: true,
      title: t("Change your solution?"),
      content: t(
        "Switching solutions now will reset your onboarding progress. You'll need to redo the remaining steps for your new selection."
      ),
      okText: t("Reset and continue"),
      cancelText: t("Cancel"),
      okButtonProps: { size: "large", style: { width: "auto" } },
      cancelButtonProps: { size: "large", style: { width: "auto" } },
      onOk: () => resetAndProceed(selected)
    })
  }

  return (
    <div className="ob-features">
      <div className="ob-features__intro">
        <h2 className="ob-features__title">{t("Choose your features")}</h2>
        <Text className="ob-features__subtitle" type="secondary">
          {t(
            "Select the Chargeback solutions you want to set up now. You can add the other one later: Recover can be set up anytime from Disputes → Configure in the sidebar."
          )}
        </Text>
      </div>

      <Flex className="ob-features__cards" gap={16} wrap="wrap">
        {FEATURE_OPTIONS.map((option) => {
          const checked = selected.includes(option.key)

          return (
            <div
              key={option.key}
              role="checkbox"
              aria-checked={checked}
              tabIndex={0}
              className={cls("feature-card", { "feature-card--active": checked })}
              onClick={() => toggle(option.key)}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault()
                  toggle(option.key)
                }
              }}
            >
              <Flex justify="space-between" align="flex-start">
                <div className="feature-card__icon">
                  <Icon type={option.icon} style={{ fontSize: 20 }} />
                </div>
                <Checkbox type="bounce" checked={checked} onChange={() => {}} />
              </Flex>
              <Text strong className="feature-card__title">
                {t(option.title)}
              </Text>
              <Text type="secondary" className="feature-card__description">
                {t(option.description)}
              </Text>
              {option.videoUrl && (
                <ButtonComponent
                  type="text"
                  size="small"
                  icon={<Icon type="play" style={{ fontSize: 16 }} />}
                  className="feature-card__video flex center"
                  onClick={(e) => {
                    e.stopPropagation()
                    setVideoUrl(option.videoUrl)
                  }}
                >
                  {t("Watch video")}
                </ButtonComponent>
              )}
            </div>
          )
        })}
      </Flex>

      <ButtonComponent
        type="primary"
        size="large"
        className="ob-features__continue flex center"
        disabled={selected.length === 0}
        onClick={handleContinue}
      >
        {t("Continue")}
      </ButtonComponent>

      <Modal
        classNames={{ wrapper: "video-modal", content: "clear-bg" }}
        open={!!videoUrl}
        centered
        closeIcon={<Icon type="close_modal" />}
        footer={null}
        onCancel={() => setVideoUrl(null)}
        destroyOnClose
      >
        <ReactPlayer controls width="100%" height="100%" playing url={videoUrl} />
      </Modal>
    </div>
  )
}

export default OnboardingFeaturesPage
